import React from 'react';
import '@blocknote/core/fonts/inter.css';
import { BlockNoteSchema } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';
import { useCreateBlockNote } from '@blocknote/react';
import { getDefaultReactSlashMenuItems } from '@blocknote/react';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import api from '../../services/api';
import { ToastService } from '../../services/ToastService';
import './BlockNoteEditorWrapper.css';
import './BlockNoteMenuFix.css';
// Equation block temporarily disabled - causes schema initialization error
// import { createEquation } from './blocks/EquationBlock';
// import { EquationSlashMenuItem } from './blocks/EquationSlashMenuItem';

interface BlockNoteEditorWrapperProps {
  nodeId: string;
  initialContent: any;
  onContentChange: (content: any) => void;
  enableComments?: boolean;
  currentUserId?: string;
  theme?: 'light' | 'dark';
}

/**
 * BlockNote Editor Wrapper Component
 * Provides a rich-text editor for node documents with:
 * - Slash commands (type / for block menu)
 * - Image uploads via MinIO/S3
 * - Auto-save
 */
export const BlockNoteEditorWrapper: React.FC<BlockNoteEditorWrapperProps> = ({
  nodeId,
  initialContent,
  onContentChange,
  enableComments = false,
  currentUserId,
  theme = 'light',
}) => {
  // Resolve users function for comments
  const resolveUsers = React.useCallback(async (userIds: string[]) => {
    try {
      const response = await api.get('/users');
      const allUsers = response.data || [];
      
      return userIds.map(id => {
        const user = allUsers.find((u: any) => u.id === id || u.stytchMemberId === id);
        return {
          id,
          username: user?.name || user?.email || 'Unknown User',
          avatarUrl: user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=random`,
        };
      });
    } catch (error) {
      console.error('Error resolving users:', error);
      return userIds.map(id => ({
        id,
        username: 'Unknown User',
        avatarUrl: `https://ui-avatars.com/api/?name=U&background=random`,
      }));
    }
  }, []);

  // Create custom schema - temporarily without equation block to isolate issue
  const schema = React.useMemo(() => {
    // Just use default schema for now
    return BlockNoteSchema.create();
  }, []);

  // Create editor instance with configuration
  const editor = useCreateBlockNote({
    schema,
    initialContent: initialContent && initialContent.length > 0 ? initialContent : undefined,
    // Enable comments if requested
    ...(enableComments && currentUserId ? {
      resolveUsers,
      // Note: ThreadStore requires additional backend support
      // For now, comments appear in the sidebar instead
    } : {}),
    uploadFile: async (file: File) => {
      try {
        console.log('📤 Uploading file:', file.name, file.type);
        
        // Create FormData and upload to backend
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/users/upload/file', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data?.fileUrl) {
          console.log('✅ File uploaded:', response.data.fileUrl);
          ToastService.success(`Uploaded ${file.name}`);
          return response.data.fileUrl;
        }
        
        throw new Error('No file URL in response');
      } catch (error: any) {
        console.error('❌ File upload failed:', error);
        ToastService.error(`Failed to upload ${file.name}`);
        // Return placeholder to prevent editor crash
        return 'https://via.placeholder.com/400?text=Upload+Failed';
      }
    },
  });

  // Debug logging and automatic menu fixing
  React.useEffect(() => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔍 BLOCKNOTE EDITOR MOUNTED');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('Editor Info:', {
      hasEditor: !!editor,
      editorDomElement: editor?.domElement,
      initialContentLength: initialContent?.length,
      nodeId,
    });
    
    // AUTO-FIX: Watch for menu appearing and fix it automatically
    const observer = new MutationObserver((mutations) => {
      const menu = document.querySelector('.bn-suggestion-menu');
      if (menu && !(menu as any)._fixed) {
        console.log('🎯 SLASH MENU DETECTED - AUTO-FIXING...');
        
        const menuEl = menu as HTMLElement;
        
        // Mark as fixed to avoid re-fixing
        (menu as any)._fixed = true;
        
        // Fix menu - force flex display
        menuEl.style.setProperty('display', 'flex', 'important');
        menuEl.style.setProperty('flex-direction', 'column', 'important');
        menuEl.style.minWidth = '300px';
        menuEl.style.maxHeight = '400px';
        menuEl.style.overflow = 'auto';
        menuEl.style.padding = '4px';
        
        // Fix ALL parent containers - AGGRESSIVELY remove constraints
        let parent = menu.parentElement;
        let level = 0;
        while (parent && level < 10) {
          const parentEl = parent as HTMLElement;
          
          // Get current inline styles
          const currentStyle = parentEl.getAttribute('style') || '';
          
          // Remove problematic inline styles
          let newStyle = currentStyle
            .replace(/max-height:\s*[^;]+;?/gi, '')
            .replace(/min-height:\s*[^;]+;?/gi, '')
            .replace(/overflow:\s*[^;]+;?/gi, '');
          
          // Add our fixes
          newStyle += ' overflow: visible !important; max-height: none !important;';
          
          parentEl.setAttribute('style', newStyle);
          
          // Log what we fixed
          if (parent.hasAttribute('data-floating-ui-focusable')) {
            console.log(`  🎯 FIXED FLOATING-UI CONTAINER at level ${level}!`);
          }
          
          parent = parent.parentElement;
          level++;
        }
        
        console.log(`  Fixed ${level} parent levels`);
        
        // Fix menu items
        Array.from(menu.children).forEach((item) => {
          const itemEl = item as HTMLElement;
          itemEl.style.setProperty('display', 'flex', 'important');
          itemEl.style.setProperty('flex-direction', 'row', 'important');
          itemEl.style.alignItems = 'center';
          itemEl.style.padding = '8px 12px';
          itemEl.style.width = '100%';
          itemEl.style.minHeight = '36px';
          itemEl.style.margin = '0 0 2px 0';
        });
        
        console.log('✅ AUTO-FIX APPLIED! Menu should be readable.');
      }
    });
    
    // Watch the entire document for menu appearing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    
    return () => {
      observer.disconnect();
    };
    
    // Add keyboard listener to detect "/" key and inspect menu
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/') {
        console.log('⌨️  SLASH KEY PRESSED - Menu should appear!');
        setTimeout(() => {
          const menu = document.querySelector('.bn-suggestion-menu, [class*="suggestion"]');
          console.log('Menu after slash:', menu ? 'FOUND' : 'NOT FOUND', menu?.className);
          
          if (menu) {
            console.log('━━━━━━━━━━━━━━━ MENU INSPECTION ━━━━━━━━━━━━━━━');
            const styles = window.getComputedStyle(menu);
            console.log('Menu computed styles:', {
              display: styles.display,
              flexDirection: styles.flexDirection,
              position: styles.position,
              zIndex: styles.zIndex,
              width: styles.width,
              height: styles.height,
              overflow: styles.overflow,
            });
            
            console.log('Menu children count:', menu.children.length);
            
            // Inspect first 3 items
            Array.from(menu.children).slice(0, 3).forEach((item, i) => {
              const itemStyles = window.getComputedStyle(item as Element);
              console.log(`Item ${i}:`, {
                tagName: item.tagName,
                className: item.className,
                text: item.textContent?.substring(0, 30),
                computedStyles: {
                  display: itemStyles.display,
                  position: itemStyles.position,
                  width: itemStyles.width,
                  height: itemStyles.height,
                  padding: itemStyles.padding,
                  margin: itemStyles.margin,
                  flexDirection: itemStyles.flexDirection,
                },
                innerHTML: (item as HTMLElement).innerHTML.substring(0, 150),
              });
            });
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          }
        }, 200);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    
    // Check if menus are in DOM after mount
    setTimeout(() => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔍 CHECKING FOR BLOCKNOTE MENUS');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      
      const menus = document.querySelectorAll('[class*="mantine"], [class*="suggestion"], [class*="slash"], [class*="Menu"]');
      console.log('Menus found:', menus.length);
      console.log('Menu classes:', Array.from(menus).map(m => ({
        class: m.className,
        tag: m.tagName,
        visible: window.getComputedStyle(m).display !== 'none',
        zIndex: window.getComputedStyle(m).zIndex,
      })));
      
      // Check all BlockNote elements
      const bnElements = document.querySelectorAll('[class*="bn-"]');
      console.log('BlockNote elements (bn-*):', bnElements.length);
      console.log('BlockNote classes:', Array.from(bnElements).slice(0, 10).map(e => e.className));
      
      // Check z-index issues
      const editorWrapper = document.querySelector('.blocknote-editor-wrapper');
      if (editorWrapper) {
        const styles = window.getComputedStyle(editorWrapper);
        console.log('Editor wrapper computed styles:', {
          zIndex: styles.zIndex,
          position: styles.position,
          overflow: styles.overflow,
          height: styles.height,
        });
      }
      
      // Check for Mantine portals
      const portals = document.querySelectorAll('[data-mantine-portal]');
      console.log('Mantine portals:', portals.length);
      portals.forEach((portal, i) => {
        console.log(`Portal ${i}:`, {
          class: portal.className,
          children: portal.children.length,
          display: window.getComputedStyle(portal).display,
          zIndex: window.getComputedStyle(portal).zIndex,
        });
      });
      
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('💡 TIP: Type "/" in the editor to trigger slash menu');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔧 To fix menu styling, run: window.fixSlashMenu()');
      
      // Create a global function to inspect and fix the menu
      (window as any).inspectMenu = () => {
        const menu = document.querySelector('.bn-suggestion-menu');
        if (!menu) {
          console.log('❌ Menu not found. Type "/" first to open it.');
          return;
        }
        
        console.log('🔍 INSPECTING MENU AND PARENTS...');
        
        // Check menu position
        const menuRect = menu.getBoundingClientRect();
        console.log('Menu position:', {
          top: menuRect.top,
          left: menuRect.left,
          width: menuRect.width,
          height: menuRect.height,
          bottom: menuRect.bottom,
          right: menuRect.right,
        });
        
        // Walk up the parent chain
        let el = menu.parentElement;
        let level = 1;
        while (el && level < 10) {
          const styles = window.getComputedStyle(el);
          const rect = el.getBoundingClientRect();
          console.log(`Parent ${level} (${el.tagName}.${el.className.substring(0, 50)}):`, {
            overflow: styles.overflow,
            overflowY: styles.overflowY,
            position: styles.position,
            zIndex: styles.zIndex,
            height: styles.height,
            maxHeight: styles.maxHeight,
            clip: styles.clip,
            clipPath: styles.clipPath,
            rect: { width: rect.width, height: rect.height },
          });
          el = el.parentElement;
          level++;
        }
      };
      
      // Create a global function to fix the menu
      (window as any).fixSlashMenu = () => {
        const menu = document.querySelector('.bn-suggestion-menu');
        if (!menu) {
          console.log('❌ Menu not found. Type "/" first to open it.');
          return;
        }
        
        console.log('🔧 Applying fixes to menu...');
        const menuEl = menu as HTMLElement;
        
        // Fix menu container - FORCE block display first
        menuEl.style.setProperty('display', 'flex', 'important');
        menuEl.style.setProperty('flex-direction', 'column', 'important');
        menuEl.style.gap = '0';
        menuEl.style.minWidth = '300px';
        menuEl.style.maxHeight = '400px';
        menuEl.style.zIndex = '10000'; // Above editor
        menuEl.style.position = 'relative';
        
        // Fix editor z-index (it was covering the menu!)
        const editor = document.querySelector('.ProseMirror');
        if (editor) {
          (editor as HTMLElement).style.zIndex = '1';
        }
        
        // Fix parent containers that might be clipping
        let parent = menu.parentElement;
        let level = 0;
        while (parent && level < 5) {
          parent.style.overflow = 'visible';
          parent.style.position = 'relative';
          console.log(`  Fixed parent ${level}: ${parent.tagName}`);
          parent = parent.parentElement;
          level++;
        }
        
        // Fix each menu item
        Array.from(menu.children).forEach((item, i) => {
          const itemEl = item as HTMLElement;
          itemEl.style.setProperty('display', 'flex', 'important');
          itemEl.style.setProperty('flex-direction', 'row', 'important');
          itemEl.style.alignItems = 'center';
          itemEl.style.gap = '8px';
          itemEl.style.padding = '8px 12px';
          itemEl.style.minHeight = '36px';
          itemEl.style.width = '100%';
          itemEl.style.boxSizing = 'border-box';
          itemEl.style.position = 'relative';
          itemEl.style.margin = '0 0 2px 0';
          
          console.log(`✅ Fixed item ${i}: ${item.textContent?.substring(0, 30)}`);
        });
        
        console.log('✅ Menu fixed! Items should be readable now.');
      };
      
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }, 2000);
  }, [editor]);

  // Handle content changes
  // BlockNote calls this automatically when content changes
  const handleChange = () => {
    // Get the current document as an array of blocks
    const content = editor.document;
    onContentChange(content);
  };

  // Auto-focus editor on mount
  React.useEffect(() => {
    if (editor) {
      setTimeout(() => {
        editor.focus();
        console.log('✨ Editor focused');
        console.log('📋 Available block specs:', Object.keys(editor.schema.blockSpecs));
        console.log('✅ Equation block registered:', 'equation' in editor.schema.blockSpecs);
      }, 100);
    }
  }, [editor]);

  // Prepare slash menu items - equation disabled for now
  const slashMenuItems = React.useMemo(() => {
    if (!editor) {
      return [];
    }
    
    try {
      const defaultItems = getDefaultReactSlashMenuItems(editor);
      console.log('📝 Slash menu items prepared:', {
        defaultCount: defaultItems.length,
      });
      return defaultItems;
    } catch (error) {
      console.error('❌ Error preparing slash menu items:', error);
      return editor ? getDefaultReactSlashMenuItems(editor) : [];
    }
  }, [editor]);

  return (
    <MantineProvider>
      <div className="blocknote-editor-wrapper">
        <BlockNoteView 
          editor={editor} 
          onChange={handleChange}
          theme={theme as 'light' | 'dark'}
          slashMenu={slashMenuItems}
        >
          {/* Slash menu is a child component - should render automatically */}
        </BlockNoteView>
      </div>
    </MantineProvider>
  );
};

